import {NativeModules, Platform} from 'react-native';

export type NotificationSoundKey = string;

export type NotificationSoundOption = {
  key: NotificationSoundKey;
  label: string;
  description: string;
  channelId: string;
  soundName: string;
  androidSoundUri?: string | null;
  previewTitle: string;
  previewMessage: string;
};

type NativeSystemSound = {
  key: string;
  label: string;
};

export const DEFAULT_NOTIFICATION_SOUND_KEY: NotificationSoundKey = 'alert';
export const DEFAULT_PHONE_SOUND_KEY: NotificationSoundKey = 'default';

const BUILTIN_NOTIFICATION_SOUNDS: NotificationSoundOption[] = [
  {
    key: DEFAULT_NOTIFICATION_SOUND_KEY,
    label: 'App alert sound',
    description: 'Uses the bundled Asterix alert tone.',
    channelId: 'RESIDENT_ID',
    soundName: 'alert.mp3',
    androidSoundUri: null,
    previewTitle: 'App alert sound',
    previewMessage: 'This is the bundled alert tone.',
  },
  {
    key: DEFAULT_PHONE_SOUND_KEY,
    label: 'Default phone sound',
    description: "Uses the device's default notification sound.",
    channelId: 'RESIDENT_DEFAULT_ID',
    soundName: 'default',
    androidSoundUri: null,
    previewTitle: 'Default phone sound',
    previewMessage: 'This is your device default notification sound.',
  },
];

const BRIDGE_NAME = 'NotificationSoundModule';

const simpleHash = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

export const buildAndroidNotificationChannelId = (soundKey: string) =>
  `RESIDENT_${simpleHash(soundKey)}`;

const isBuiltinSound = (soundKey?: string | null) =>
  soundKey === DEFAULT_NOTIFICATION_SOUND_KEY ||
  soundKey === DEFAULT_PHONE_SOUND_KEY;

export const resolveNotificationSound = (
  soundKey?: NotificationSoundKey | null,
  label?: string,
): NotificationSoundOption => {
  const builtin = BUILTIN_NOTIFICATION_SOUNDS.find(
    option => option.key === soundKey,
  );

  if (builtin) {
    return builtin;
  }

  const resolvedKey = soundKey ?? DEFAULT_NOTIFICATION_SOUND_KEY;
  const resolvedLabel = label ?? resolvedKey;

  return {
    key: resolvedKey,
    label: resolvedLabel,
    description: `System ringtone: ${resolvedLabel}`,
    channelId: buildAndroidNotificationChannelId(resolvedKey),
    soundName: 'default',
    androidSoundUri: resolvedKey,
    previewTitle: resolvedLabel,
    previewMessage: `This is the ${resolvedLabel} ringtone.`,
  };
};

export const getBaseNotificationSoundOptions = () => [
  ...BUILTIN_NOTIFICATION_SOUNDS,
];

export const getNotificationSoundOption = (
  soundKey?: NotificationSoundKey | null,
  options: NotificationSoundOption[] = BUILTIN_NOTIFICATION_SOUNDS,
): NotificationSoundOption => {
  return (
    options.find(option => option.key === soundKey) ??
    BUILTIN_NOTIFICATION_SOUNDS.find(
      option => option.key === DEFAULT_NOTIFICATION_SOUND_KEY,
    )!
  );
};

export const getNotificationSoundOptions = async (): Promise<
  NotificationSoundOption[]
> => {
  if (Platform.OS !== 'android') {
    return getBaseNotificationSoundOptions();
  }

  const nativeModule = NativeModules[BRIDGE_NAME];
  if (!nativeModule?.getSystemNotificationSounds) {
    return getBaseNotificationSoundOptions();
  }

  try {
    const nativeSounds: NativeSystemSound[] =
      await nativeModule.getSystemNotificationSounds();

    const mappedSounds = nativeSounds
      .filter(
        sound =>
          !!sound?.key &&
          !isBuiltinSound(sound.key) &&
          sound.key !== 'null',
      )
      .map(sound => {
        const resolvedLabel = sound.label || sound.key;
        return {
          key: sound.key,
          label: resolvedLabel,
          description: `System ringtone: ${resolvedLabel}`,
          channelId: buildAndroidNotificationChannelId(sound.key),
          soundName: 'default',
          androidSoundUri: sound.key,
          previewTitle: resolvedLabel,
          previewMessage: `This is the ${resolvedLabel} ringtone.`,
        };
      });

    const combined = [...BUILTIN_NOTIFICATION_SOUNDS, ...mappedSounds];
    const seen = new Set<string>();
    return combined.filter(option => {
      if (seen.has(option.key)) {
        return false;
      }
      seen.add(option.key);
      return true;
    });
  } catch (error) {
    console.log('getNotificationSoundOptions failed', error);
    return getBaseNotificationSoundOptions();
  }
};

export const ensureNotificationChannelForSoundKey = async (
  soundKey: NotificationSoundKey,
  label?: string,
) => {
  if (Platform.OS !== 'android') {
    return;
  }

  const sound = resolveNotificationSound(soundKey, label);

  const nativeModule = NativeModules[BRIDGE_NAME];
  if (sound.androidSoundUri && nativeModule?.createNotificationChannel) {
    await nativeModule.createNotificationChannel({
      channelId: sound.channelId,
      channelName: sound.label,
      channelDescription: sound.description,
      soundUri: sound.androidSoundUri,
      importance: 4,
      vibrate: true,
    });
    return;
  }

  const PushNotification = require('react-native-push-notification');
  const {Importance} = PushNotification;
  await new Promise<void>(resolve => {
    PushNotification.createChannel(
      {
        channelId: sound.channelId,
        channelName: sound.label,
        channelDescription: sound.description,
        playSound: true,
        soundName: sound.soundName,
        importance: Importance.HIGH,
        vibrate: true,
      },
      () => resolve(),
    );
  });
};
